import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import GroupAddIcon from '@mui/icons-material/GroupAdd'
import { Box, Button, Checkbox, Collapse, Divider, FormControlLabel, IconButton, TextField, Typography } from '@mui/material'
import * as React from 'react'
import { useNavigate } from 'react-router'

import ConfirmationDialog from './ConfirmationDialog'
import RegistrationReminder from './RegistrationReminder'
import { WAIVER_TEXT } from '../../share/Constant'
import { validateUserProfileForRegistration } from '../../utils/profileValidation'
import useBar from '../common/Bar'
import InfoSection from '../common/InfoSection'

/**
 * A reusable confirmation dialog component
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Object} props.user - User object
 * @param {function} props.onClose - Handler for closing the dialog
 * @param {function} props.onConfirm - Handler for confirming the deletion
 * @param {string} props.title - Dialog title (optional)
 * @param {string} props.content - Dialog content text (optional)
 * @param {React.ReactNode} props.children - Optional children to render inside the dialog content
 * @param {Object} props.actions - Actions to render in the dialog actions
 * @param {Object} props.actions.cancel - Cancel action
 * @param {string} props.actions.cancel.label - Cancel button label
 * @param {function} props.actions.cancel.onClick - Cancel button click handler
 * @param {string} props.actions.confirm.label - Confirm button label
 * @param {function} props.actions.confirm.onClick - Confirm button click handler
 */
export default function RegistrationConfirmationDialog({
  session,
  open,
  user,
  title = 'Confirm Registration',
  content = 'You are about to book the following session: ',
  actions = {
    cancel: {
      label: 'Cancel',
      onClick: () => {},
      color: 'primary',
    },
    confirm: {
      label: 'Confirm Booking',
      onClick: () => {},
      color: 'primary',
      variant: 'contained',
      submitting: false,
    },
  },
}) {
  const navigate = useNavigate()
  const bar = useBar()
  const [notes, setNotes] = React.useState('')
  const [waiverExpanded, setWaiverExpanded] = React.useState(true)
  const [waiverAgreed, setWaiverAgreed] = React.useState(false)
  const isProfileValid = validateUserProfileForRegistration(user).isValid

  // Capacity control
  const maxParticipants = Number(session?.maxParticipants) || 0
  const currentParticipants = Array.isArray(session?.participants) ? session.participants.length : 0
  const remainingSpots = maxParticipants > 0 ? Math.max(0, maxParticipants - currentParticipants) : Infinity

  // Build registrant options: self + family members (if any)
  const registrantOptions = React.useMemo(() => {
    return (user?.familyMembers || []).map((m) => ({
      id: user?.resourceId,
      familyId: m.familyId,
      label: `${m.name}${m.familyId !== 0 ? ` (${m.relation.replace(/_/g, ' ')})` : ''}`.trim(),
    }))
  }, [user])

  const [selectedRegistrantKeys, setSelectedRegistrantKeys] = React.useState([0])
  const [capacityWarning, setCapacityWarning] = React.useState('')

  const toggleRegistrant = (key) => {
    setSelectedRegistrantKeys((prev) => {
      const set = new Set(prev)
      const isAdding = !set.has(key)
      // Capacity control while selecting
      if (isAdding && Number.isFinite(remainingSpots) && set.size >= remainingSpots) {
        setCapacityWarning(`Only ${remainingSpots} spots left`)
        window.clearTimeout(toggleRegistrant.__t)
        toggleRegistrant.__t = window.setTimeout(() => setCapacityWarning(''), 2000)
        return Array.from(set)
      }

      if (set.has(key)) {
        set.delete(key)
      } else {
        set.add(key)
      }
      return Array.from(set)
    })
  }

  const wrappedActions = {
    ...actions,
    cancel: {
      ...actions.cancel,
      onClick: () => {
        setNotes('')
        setWaiverAgreed(false)
        actions.cancel.onClick()
      },
    },
    confirm: {
      ...actions.confirm,
      onClick: () => {
        // Add check for registrants, must be selected at least one registrant
        if (selectedRegistrantKeys.length === 0) {
          bar.error('Please select at least one registrant')
          return
        }

        const participants = selectedRegistrantKeys.map((key) => {
          const registrant = user?.familyMembers?.find((m) => m.familyId === key)
          return { id: user?.resourceId, familyId: registrant.familyId }
        })

        // Hack, we don't support notes yet, so I put note in to first participant
        if (participants.length > 0) {
          participants[0].note = notes
        }

        actions.confirm.onClick({ notes, participants })
        setNotes('')
        setWaiverAgreed(false)
      },
      disabled: !waiverAgreed || !isProfileValid || selectedRegistrantKeys.length === 0 || (Number.isFinite(remainingSpots) && selectedRegistrantKeys.length > remainingSpots),
    },
  }

  return (
    <ConfirmationDialog open={open} title={title} content={content} actions={wrappedActions} fullScreenOnSmall>
      {/* Session Details */}
      <Box sx={{ mb: 3, mt: 1 }}>
        <Box sx={{ pl: { xs: 0, md: 2 } }}>
          <Typography variant="h5" sx={{ mb: 1 }}>
            <strong>Group Session:</strong> {session.title}
          </Typography>

          <Divider sx={{ my: 2 }} />
          <InfoSection
            info={{
              showSpotsTaken: false,
              location: session.skiResort,
              date: session.date,
              availableSpots: remainingSpots,
              price: session.price,
            }}
            columns={1}
            hideLabels={false}
            sx={{ maxWidth: '600px', mx: 'auto', width: '100%' }}
          />
        </Box>
      </Box>

      {/* Contact Information Reminder */}
      <RegistrationReminder user={user} />

      {/* Register For (Self or Family Members) */}
      <Divider sx={{ my: 2 }} />
      <Box sx={{ mt: 2 }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Register for
        </Typography>
        <Box sx={{ maxWidth: '600px', mx: 'auto', width: '100%', pl: 2 }}>
          {registrantOptions.map((opt) => {
            const key = opt.familyId
            const checked = selectedRegistrantKeys.includes(key)
            return (
              <FormControlLabel
                key={key}
                control={<Checkbox checked={checked} onChange={() => toggleRegistrant(key)} color="primary" />}
                label={
                  <Typography variant="body2" color="text.secondary">
                    {opt.label}
                  </Typography>
                }
              />
            )
          })}
        </Box>
        {capacityWarning && (
          <Box sx={{ mt: 0.5 }}>
            <Typography variant="caption" color="error.main">
              {capacityWarning}
            </Typography>
          </Box>
        )}
        <Box sx={{ mt: 1 }}>
          <Button size="small" variant="outlined" color="info" startIcon={<GroupAddIcon />} onClick={() => navigate('/profile')}>
            Edit family members
          </Button>
        </Box>
      </Box>

      {/* Waiver Section */}
      <Divider sx={{ my: 2 }} />
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" color="text.secondary">
            Waiver and Release of Liability
          </Typography>
          <IconButton onClick={() => setWaiverExpanded(!waiverExpanded)} size="small">
            {waiverExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        <Collapse in={waiverExpanded}>
          <Box
            sx={{
              mt: 1,
              p: 2,
              bgcolor: 'warning.lighter',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'warning.light',
            }}
          >
            <Typography variant="h7" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
              ACKNOWLEDGEMENT, WAIVER AND RELEASE OF LIABILITY
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
              {WAIVER_TEXT}
            </Typography>
          </Box>
        </Collapse>

        <FormControlLabel
          control={
            <Checkbox
              checked={waiverAgreed}
              onChange={(e) => {
                setWaiverAgreed(e.target.checked)
                setWaiverExpanded(!e.target.checked)
              }}
              color="primary"
            />
          }
          label={
            <Typography variant="body2" color="text.secondary">
              I have read and agree to the waiver agreement{' '}
              <Box component="span" sx={{ color: 'error.main' }}>
                *
              </Box>
            </Typography>
          }
          sx={{ mt: 1 }}
        />
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Session Notes */}
      <Box>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Session Notes: <br />
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Sending a note to your instructor can help them prepare for the session.
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={6}
          margin="normal"
          placeholder="e.g. I want to improve my carving skills during this session."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          aria-label="Session notes"
          sx={{
            mt: 3,
            '& .MuiInputBase-root': {
              minHeight: '150px',
            },
            '& .MuiOutlinedInput-root': {
              padding: '16px',
            },
          }}
        />
      </Box>
    </ConfirmationDialog>
  )
}
